import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api'
import type {
  Comentario,
  ComentarioEntrada,
  PanelPublicacion,
  VersionEntrada,
} from '@/lib/schemas/revision'

const CLAVES = {
  panel: (id: string, version: number | null) => ['panel', id, version] as const,
}

const jsonInit = (cuerpo?: unknown): RequestInit => ({
  method: 'POST',
  ...(cuerpo === undefined
    ? {}
    : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cuerpo) }),
})

export function usePanel(id: string | null, version: number | null) {
  const api = useApi()
  return useQuery({
    queryKey: CLAVES.panel(id ?? '', version),
    queryFn: () =>
      api<PanelPublicacion>(
        `/api/revision/${id ?? ''}${version ? `?version=${String(version)}` : ''}`,
      ),
    enabled: Boolean(id),
  })
}

function useInvalidar() {
  const qc = useQueryClient()
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['panel'] }),
      qc.invalidateQueries({ queryKey: ['grilla'] }),
      qc.invalidateQueries({ queryKey: ['contadores'] }),
    ])
  }
}

export function useComentar(id: string) {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (datos: ComentarioEntrada) =>
      api<Comentario[]>(`/api/revision/${id}/comentarios`, jsonInit(datos)),
    onSuccess: invalidar,
  })
}

export function useDecidir(id: string) {
  const api = useApi()
  const invalidar = useInvalidar()
  return {
    aprobar: useMutation({
      mutationFn: () => api<null>(`/api/revision/${id}/aprobar`, jsonInit()),
      onSuccess: invalidar,
    }),
    pedirCambios: useMutation({
      mutationFn: () => api<null>(`/api/revision/${id}/cambios`, jsonInit()),
      onSuccess: invalidar,
    }),
    deshacer: useMutation({
      mutationFn: () => api<null>(`/api/revision/${id}/deshacer`, jsonInit()),
      onSuccess: invalidar,
    }),
  }
}

export function useMarcarResuelto() {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: ({
      comentarioId,
      resuelto,
    }: {
      comentarioId: string
      resuelto: boolean
    }) =>
      api<null>(
        `/api/revision/comentarios/${comentarioId}/resuelto`,
        jsonInit({ resuelto }),
      ),
    onSuccess: invalidar,
  })
}

export function useCrearVersion(id: string) {
  const api = useApi()
  const invalidar = useInvalidar()
  return useMutation({
    mutationFn: (datos: VersionEntrada) =>
      api<{ numero: number; aprobacionAutomatica: boolean }>(
        `/api/revision/${id}/versiones`,
        jsonInit(datos),
      ),
    onSuccess: invalidar,
  })
}
